import { Helmet } from "react-helmet";
import { useState, useEffect, useRef } from "react";
import {
  FaPlus,
  FaMagnifyingGlass,
  FaUser,
  FaChevronLeft,
  FaChevronRight,
  FaAnglesLeft,
  FaAnglesRight,
  FaFilter,
  FaWifi,
  FaFileExport,
  FaFileImport,
  FaCheck,
  FaRotateLeft,
  FaXmark,
  FaArrowDownShortWide,
  FaArrowUpShortWide,
} from "react-icons/fa6";
import { BtnDefault } from "../../../../components/buttons/BtnDefault";
import { Header } from "../../../../components/Header";
import { DashboardLayout } from "../../../../components/layouts/DashboardLayout";
import { useHookPppoeUser } from "../../../../hooks/useHookPppoeUser";
import { useNavigate } from "react-router-dom";
import { SelectNas } from "../../../../components/forms/selects/SelectNas";
import { SelectOdp } from "../../../../components/forms/selects/SelectOdp";
import { SelectPop } from "../../../../components/forms/selects/SelectPop";
import { SelectOdc } from "../../../../components/forms/selects/SelectOdc";
import { SelectProfile } from "../../../../components/forms/selects/SelectProfile";
import { PppoeUserService } from "../../../../services/PppoeUserService";
import { PppoeUpdateStatus } from "./modal/UpdateStatus";
import { useCopyText } from "../../../../hooks/useHookCopyText";
import { MdOutlineWifiOff } from "react-icons/md";
import { ImportPppoeUser } from "./modal/importData";
import Pagination from "../../../../components/pagination/pagination";
import { InternetStatus } from "../../../../components/status/internet_status";
import { SelectCentralOdc } from "../../../../components/forms/selects/SelectCentralOdc";
import { SelectOlt } from "../../../../components/forms/selects/SelectOlt";

/* ============================================
   KOMPONEN: Skeleton
   ============================================ */
const SkeletonRow = ({ cols = 10 }) => (
  <tr className="animate-pulse">
    {Array.from({ length: cols }).map((_, i) => (
      <td key={i} className="px-4 py-3">
        <div
          className="h-3.5 bg-slate-700/60 rounded-md"
          style={{ width: `${40 + Math.random() * 50}%` }}
        />
      </td>
    ))}
  </tr>
);

const SkeletonCard = () => (
  <div
    className="rounded-2xl p-5 animate-pulse"
    style={{
      background: "rgba(15, 23, 42, 0.5)",
      border: "1px solid rgba(51, 65, 85, 0.3)",
    }}
  >
    <div className="flex justify-between items-start mb-4">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-full bg-slate-700/60" />
        <div className="space-y-2">
          <div className="h-4 bg-slate-700/60 rounded-md w-28" />
          <div className="h-3 bg-slate-700/40 rounded-md w-20" />
        </div>
      </div>
      <div className="h-6 bg-slate-700/40 rounded-full w-16" />
    </div>
    <div
      className="grid grid-cols-2 gap-3 pt-3"
      style={{ borderTop: "1px solid rgba(51, 65, 85, 0.2)" }}
    >
      {[...Array(4)].map((_, i) => (
        <div key={i} className="space-y-1.5">
          <div className="h-2.5 bg-slate-700/30 rounded w-14" />
          <div className="h-3.5 bg-slate-700/50 rounded w-20" />
        </div>
      ))}
    </div>
  </div>
);

/* ============================================
   KOMPONEN: Wifi Signal
   ============================================ */

/* ============================================
   KOMPONEN: CopyToast
   ============================================ */
const CopyToast = ({ show, label }) => {
  if (!show) return null;
  return (
    <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[9999] pointer-events-none">
      <div
        className="flex items-center gap-2.5 px-5 py-3 rounded-xl shadow-2xl shadow-emerald-500/10 border border-emerald-500/20"
        style={{
          background:
            "linear-gradient(135deg, rgba(6, 78, 59, 0.92), rgba(15, 23, 42, 0.95))",
          backdropFilter: "blur(16px)",
          animation: "copyToastIn 0.35s cubic-bezier(0.16, 1, 0.3, 1) forwards",
        }}
      >
        <span className="flex items-center justify-center w-6 h-6 rounded-full bg-emerald-500/20">
          <FaCheck className="text-emerald-400 text-xs" />
        </span>
        <span className="text-sm font-medium text-emerald-300 whitespace-nowrap">
          {label} berhasil disalin
        </span>
        <style>{`
          @keyframes copyToastIn {
            0%   { opacity: 0; transform: translateY(16px) scale(0.95); }
            100% { opacity: 1; transform: translateY(0) scale(1); }
          }
        `}</style>
      </div>
    </div>
  );
};

/* ============================================
   KOMPONEN: FilterDrawer
   ============================================ */
const FILTER_DEFAULTS = {
  nas_id: "",
  pop_id: "",
  odc_id: "",
  odp_id: "",
  profile_id: "",
  status: "",
  type: "",
  sort_by: "created_at",
  sort_dir: "desc",
  per_page: 10,
};

const SORT_OPTIONS = [
  { value: "created_at", label: "Tanggal Dibuat" },
  { value: "name", label: "Nama" },
  { value: "internet_number", label: "No Internet" },
  { value: "username", label: "Username" },
  { value: "status", label: "Status" },
];

const STATUS_OPTIONS = [
  { value: "aktif", label: "Aktif" },
  { value: "nonaktif", label: "Nonaktif" },
];

const TYPE_OPTIONS = [
  { value: "pppoe", label: "PPPoE" },
  { value: "hotspot", label: "Hotspot" },
];

const FilterDrawer = ({ open, setOpen, currentParams, onApply }) => {
  const panelRef = useRef(null);
  const [form, setForm] = useState(FILTER_DEFAULTS);

  /* Sync dari params saat buka */
  useEffect(() => {
    if (open) {
      setForm({
        nas_id: currentParams.nas_id || "",
        pop_id: currentParams.pop_id || "",
        odc_id: currentParams.odc_id || "",
        odp_id: currentParams.odp_id || "",
        profile_id: currentParams.profile_id || "",
        status: currentParams.status || "",
        type: currentParams.type || "",
        sort_by: currentParams.sort_by || "created_at",
        sort_dir: currentParams.sort_dir || "desc",
        per_page: currentParams.per_page || 10,
      });
    }
  }, [open, currentParams]);

  /* Lock body scroll */
  useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  const handleReset = () => {
    setForm(FILTER_DEFAULTS);
  };

  const handleApply = () => {
    onApply(form);
    setOpen(false);
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) setOpen(false);
  };

  /* ── Hitung field aktif (exclude sort default) ── */
  const activeCount = Object.entries(form).filter(([key, val]) => {
    if (key === "sort_by" || key === "sort_dir") return false;
    return val !== "" && val !== null && val !== undefined;
  }).length;

  return (
    <>
      {/* Backdrop */}
      <div
        className={`fixed inset-0 z-[998] bg-black/40 backdrop-blur-sm transition-opacity duration-300 ${
          open ? "opacity-100" : "opacity-0 pointer-events-none"
        }`}
        onClick={handleBackdropClick}
      />

      {/* Panel */}
      <div
        ref={panelRef}
        className={`fixed top-0 right-0 z-[999] h-full w-full max-w-md shadow-2xl transition-transform duration-300 ease-out ${
          open ? "translate-x-0" : "translate-x-full"
        }`}
        style={{
          background: "rgba(15, 23, 42, 0.98)",
          borderLeft: "1px solid rgba(51, 65, 85, 0.3)",
          backdropFilter: "blur(20px)",
        }}
      >
        {/* Header */}
        <div
          className="flex items-center justify-between px-6 py-5"
          style={{ borderBottom: "1px solid rgba(51, 65, 85, 0.25)" }}
        >
          <div className="flex items-center gap-3">
            <div
              className="w-9 h-9 rounded-xl flex items-center justify-center"
              style={{
                background: "rgba(0, 180, 216, 0.12)",
                border: "1px solid rgba(0, 180, 216, 0.2)",
              }}
            >
              <FaFilter className="text-sm text-cyan-400" />
            </div>
            <div>
              <h3 className="font-bold text-gray-100 text-sm">Filter Data</h3>
              {activeCount > 0 && (
                <p className="text-[10px] text-cyan-400 mt-0.5">
                  {activeCount} filter aktif
                </p>
              )}
            </div>
          </div>
          <button
            onClick={() => setOpen(false)}
            className="p-2 rounded-xl text-slate-400 hover:text-white hover:bg-white/10 transition-all"
          >
            <FaXmark size={18} />
          </button>
        </div>

        {/* Body — scrollable */}
        <div
          className="overflow-y-auto px-6 py-5 space-y-5"
          style={{ height: "calc(100% - 140px)" }}
        >
          {/* ── Infrastruktur ── */}
          <div>
            <p className="text-[10px] font-black text-slate-500 uppercase tracking-widest mb-3">
              Infrastruktur
            </p>
            <div className="space-y-3">
              <SelectPop
                label="Pop"
                placeholder="Silahkan pilih Pop"
                value={form.pop_id}
                setValue={(selectedId) =>
                  setForm((prev) => ({
                    ...prev,
                    pop_id: selectedId,
                    nas_id: "",
                    olt_id: "",
                    central_odc_id: "",
                    odc_id: "",
                    odp_id: "",
                  }))
                }
                searchable={true}
              />

              <SelectNas
                label="Nas Server - MikroTik"
                placeholder="Silahkan pilih Nas Server"
                pop_id={form.pop_id}
                value={form.nas_id}
                setValue={(selectedId) =>
                  setForm((prev) => ({
                    ...prev,
                    nas_id: selectedId,
                    olt_id: "",
                    central_odc_id: "",
                    odc_id: "",
                    odp_id: "",
                  }))
                }
                searchable={true}
              />

              <SelectOlt
                label="Olt"
                placeholder="Silahkan pilih Olt"
                pop_id={form.pop_id}
                nas_id={form.nas_id}
                value={form.olt_id}
                setValue={(selectedId) =>
                  setForm((prev) => ({
                    ...prev,
                    olt_id: selectedId,
                    central_odc_id: "",
                    odc_id: "",
                    odp_id: "",
                  }))
                }
                searchable={true}
              />

              <SelectCentralOdc
                label="Central ODC"
                placeholder="Silahkan pilih Central ODC"
                pop_id={form.pop_id}
                nas_id={form.nas_id}
                olt_id={form.olt_id}
                value={form.central_odc_id}
                setValue={(selectedId) =>
                  setForm((prev) => ({
                    ...prev,
                    central_odc_id: selectedId,
                    odc_id: "",
                    odp_id: "",
                  }))
                }
                searchable={true}
              />

              <SelectOdc
                label="Odc"
                placeholder="Silahkan pilih Odc"
                pop_id={form.pop_id}
                nas_id={form.nas_id}
                olt_id={form.olt_id}
                central_odc_id={form.central_odc_id}
                value={form.odc_id}
                setValue={(selectedId) =>
                  setForm((prev) => ({
                    ...prev,
                    odc_id: selectedId,
                    nas_id: "",
                    odp_id: "",
                  }))
                }
                searchable={true}
              />

              <SelectOdp
                placeholder="Semua ODP"
                pop_id={form.odc_id}
                value={form.odp_id}
                searchable
                setValue={(id) => setForm((f) => ({ ...f, odp_id: id }))}
              />
            </div>
          </div>

          {/* Divider */}
          <div style={{ borderTop: "1px dashed rgba(51, 65, 85, 0.25)" }} />

          {/* ── Layanan ── */}
          <div>
            <p className="text-[10px] font-black text-slate-500 uppercase tracking-widest mb-3">
              Layanan
            </p>
            <div className="space-y-3">
              <SelectProfile
                placeholder="Semua Profile"
                value={form.profile_id}
                searchable
                setValue={(id) => setForm((f) => ({ ...f, profile_id: id }))}
              />
            </div>
          </div>

          {/* Divider */}
          <div style={{ borderTop: "1px dashed rgba(51, 65, 85, 0.25)" }} />

          {/* ── Klasifikasi ── */}
          <div>
            <p className="text-[10px] font-black text-slate-500 uppercase tracking-widest mb-3">
              Klasifikasi
            </p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-[10px] text-slate-500 font-semibold mb-1.5 uppercase tracking-wider">
                  Status
                </label>
                <select
                  value={form.status}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, status: e.target.value }))
                  }
                  className="w-full px-3 py-2.5 text-sm rounded-xl bg-slate-800/60 border border-slate-600/40 text-gray-100 outline-none focus:border-cyan-500/50 focus:ring-1 focus:ring-cyan-500/20 transition-all appearance-none cursor-pointer"
                  style={{
                    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' fill='%2394a3b8' viewBox='0 0 16 16'%3E%3Cpath d='M1.646 4.646a.5.5 0 0 1 .708 0L8 10.293l5.646-5.647a.5.5 0 0 1 .708.708l-6 6a.5.5 0 0 1-.708 0l-6-6a.5.5 0 0 1 0-.708z'/%3E%3C/svg%3E")`,
                    backgroundRepeat: "no-repeat",
                    backgroundPosition: "right 12px center",
                  }}
                >
                  <option value="">Semua</option>
                  {STATUS_OPTIONS.map((s) => (
                    <option key={s.value} value={s.value}>
                      {s.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-[10px] text-slate-500 font-semibold mb-1.5 uppercase tracking-wider">
                  Tipe
                </label>
                <select
                  value={form.type}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, type: e.target.value }))
                  }
                  className="w-full px-3 py-2.5 text-sm rounded-xl bg-slate-800/60 border border-slate-600/40 text-gray-100 outline-none focus:border-cyan-500/50 focus:ring-1 focus:ring-cyan-500/20 transition-all appearance-none cursor-pointer"
                  style={{
                    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' fill='%2394a3b8' viewBox='0 0 16 16'%3E%3Cpath d='M1.646 4.646a.5.5 0 0 1 .708 0L8 10.293l5.646-5.647a.5.5 0 0 1 .708.708l-6 6a.5.5 0 0 1-.708 0l-6-6a.5.5 0 0 1 0-.708z'/%3E%3C/svg%3E")`,
                    backgroundRepeat: "no-repeat",
                    backgroundPosition: "right 12px center",
                  }}
                >
                  <option value="">Semua</option>
                  {TYPE_OPTIONS.map((t) => (
                    <option key={t.value} value={t.value}>
                      {t.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Divider */}
          <div style={{ borderTop: "1px dashed rgba(51, 65, 85, 0.25)" }} />

          {/* ── Pengurutan ── */}
          <div>
            <p className="text-[10px] font-black text-slate-500 uppercase tracking-widest mb-3">
              Pengurutan
            </p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-[10px] text-slate-500 font-semibold mb-1.5 uppercase tracking-wider">
                  Urutkan
                </label>
                <select
                  value={form.sort_by}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, sort_by: e.target.value }))
                  }
                  className="w-full px-3 py-2.5 text-sm rounded-xl bg-slate-800/60 border border-slate-600/40 text-gray-100 outline-none focus:border-cyan-500/50 focus:ring-1 focus:ring-cyan-500/20 transition-all appearance-none cursor-pointer"
                  style={{
                    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' fill='%2394a3b8' viewBox='0 0 16 16'%3E%3Cpath d='M1.646 4.646a.5.5 0 0 1 .708 0L8 10.293l5.646-5.647a.5.5 0 0 1 .708.708l-6 6a.5.5 0 0 1-.708 0l-6-6a.5.5 0 0 1 0-.708z'/%3E%3C/svg%3E")`,
                    backgroundRepeat: "no-repeat",
                    backgroundPosition: "right 12px center",
                  }}
                >
                  {SORT_OPTIONS.map((s) => (
                    <option key={s.value} value={s.value}>
                      {s.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-[10px] text-slate-500 font-semibold mb-1.5 uppercase tracking-wider">
                  Arah
                </label>
                <button
                  type="button"
                  onClick={() =>
                    setForm((f) => ({
                      ...f,
                      sort_dir: f.sort_dir === "desc" ? "asc" : "desc",
                    }))
                  }
                  className="w-full flex items-center justify-center gap-2 px-3 py-2.5 text-sm rounded-xl border transition-all"
                  style={{
                    background:
                      form.sort_dir === "desc"
                        ? "rgba(0, 180, 216, 0.08)"
                        : "rgba(251, 146, 60, 0.08)",
                    borderColor:
                      form.sort_dir === "desc"
                        ? "rgba(0, 180, 216, 0.2)"
                        : "rgba(251, 146, 60, 0.2)",
                    color: form.sort_dir === "desc" ? "#22d3ee" : "#fb923c",
                  }}
                >
                  {form.sort_dir === "desc" ? (
                    <>
                      <FaArrowDownShortWide size={14} />
                      Terbaru
                    </>
                  ) : (
                    <>
                      <FaArrowUpShortWide size={14} />
                      Terlama
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>

          {/* ── Per Page ── */}
          <div className="flex items-center gap-3">
            <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1.5">
              Per halaman
            </label>
            <select
              value={form.per_page}
              onChange={(e) =>
                setForm((f) => ({ ...f, per_page: e.target.value }))
              }
              className="w-full px-3 py-2.5 text-sm rounded-xl bg-slate-800/60 border border-slate-600/40 text-gray-100 outline-none focus:border-cyan-500/50 focus:ring-1 focus:ring-cyan-500/20 transition-all appearance-none cursor-pointer"
              style={{
                backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' fill='%2394a3b8' viewBox='0 0 16 16'%3E%3Cpath d='M1.646 4.646a.5.5 0 0 1 .708 0L8 10.293l5.646-5.647a.5.5 0 0 1 .708.708l-6 6a.5.5 0 0 1-.708 0l-6-6a.5.5 0 0 1 0-.708z'/%3E%3C/svg%3E")`,
                backgroundRepeat: "no-repeat",
                backgroundPosition: "right 12px center",
              }}
            >
              <option value="10">10</option>
              <option value="20">20</option>
              <option value="30">30</option>
              <option value="40">40</option>
              <option value="50">50</option>
            </select>
          </div>
        </div>

        {/* Footer */}
        <div
          className="absolute bottom-0 left-0 right-0 flex gap-3 px-6 py-4"
          style={{
            background: "rgba(15, 23, 42, 0.98)",
            borderTop: "1px solid rgba(51, 65, 85, 0.25)",
            backdropFilter: "blur(20px)",
          }}
        >
          <button
            type="button"
            onClick={handleReset}
            className="flex items-center justify-center gap-2 flex-1 px-4 py-2.5 rounded-xl text-sm font-semibold border transition-all"
            style={{
              background: "rgba(51, 65, 85, 0.15)",
              borderColor: "rgba(51, 65, 85, 0.3)",
              color: "#94a3b8",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = "rgba(239, 68, 68, 0.08)";
              e.currentTarget.style.borderColor = "rgba(239, 68, 68, 0.2)";
              e.currentTarget.style.color = "#f87171";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "rgba(51, 65, 85, 0.15)";
              e.currentTarget.style.borderColor = "rgba(51, 65, 85, 0.3)";
              e.currentTarget.style.color = "#94a3b8";
            }}
          >
            <FaRotateLeft size={13} />
            Reset
          </button>
          <BtnDefault
            type="button"
            onClick={handleApply}
            className="flex-1 justify-center py-2.5"
          >
            Terapkan
          </BtnDefault>
        </div>
      </div>
    </>
  );
};

/* ============================================
   KOMPONEN UTAMA
   ============================================ */
export const PppoeUser = () => {
  const [showSidebar, setShowSidebar] = useState(true);
  const navigate = useNavigate();

  const { data, meta, loading, refreshData, params, setParams } =
    useHookPppoeUser();

  const [selectedIds, setSelectedIds] = useState([]);
  const { copied, copy } = useCopyText();
  const [copyLabel, setCopyLabel] = useState("");

  /* ── Modal States ── */
  const [openUpdateStatus, setOpenUpdateStatus] = useState(false);
  const [selectedId, setSelectedId] = useState();
  const [selectedStatus, setSelectedStatus] = useState();

  const [openImport, setOpenImport] = useState(false);
  const [loadingExport, setLoadingExport] = useState(false);
  const [openFilter, setOpenFilter] = useState(false);

  /* ── Hitung filter aktif (untuk badge) ── */
  const activeFilterCount = [
    "nas_id",
    "pop_id",
    "odc_id",
    "odp_id",
    "profile_id",
    "status",
    "type",
  ].filter((key) => params[key] && params[key] !== "").length;

  /* ── Helper: copy dengan label ── */
  const handleCopy = (text, label) => {
    setCopyLabel(label);
    copy(text);
  };

  /* ── Helper: apply filter dari drawer ── */
  const handleFilterApply = (filterForm) => {
    setParams((prev) => ({
      ...prev,
      ...filterForm,
      page: 1,
    }));
  };

  /* ── Helper: reset filter dari luar drawer ── */
  const handleFilterReset = () => {
    setParams((prev) => ({
      ...prev,
      ...FILTER_DEFAULTS,
      page: 1,
    }));
  };

  /* ---------- Selection ---------- */
  const toggleSelect = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  const toggleSelectAll = () => {
    setSelectedIds((prev) =>
      prev.length === data.length ? [] : data.map((x) => x.id),
    );
  };

  /* ---------- Pagination ---------- */
  const getPages = () => {
    const { last_page: total, current_page: current } = meta;
    const delta = 2;
    const pages = [];
    const left = Math.max(2, current - delta);
    const right = Math.min(total - 1, current + delta);

    pages.push(1);
    if (left > 2) pages.push("...");
    for (let i = left; i <= right; i++) pages.push(i);
    if (right < total - 1) pages.push("...");
    if (total > 1) pages.push(total);
    return pages;
  };

  /* ---------- Status Badge ---------- */
  const StatusBadge = ({ status }) => {
    const colors = {
      aktif: "bg-emerald-500/12 text-emerald-400 border-emerald-500/20",
      nonaktif: "bg-red-500/12 text-red-400 border-red-500/20",
    };
    const dotColors = { aktif: "bg-emerald-500", nonaktif: "bg-red-500" };

    return (
      <span
        className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold tracking-wide border ${
          colors[status] || "bg-amber-500/12 text-amber-400 border-amber-500/20"
        }`}
      >
        <span
          className={`w-1.5 h-1.5 rounded-full ${dotColors[status] || "bg-amber-500"}`}
        />
        {status}
      </span>
    );
  };

  /* ---------- Columns ---------- */
  const columns = [
    { key: "no", label: "No", align: "center", width: "60px" },
    { key: "status", label: "Status", align: "center" },
    { key: "inet", label: "INET", align: "center", width: "50px" },
    { key: "id_number", label: "No Internet", align: "left" },
    { key: "name", label: "Nama", align: "left" },
    { key: "profile", label: "Profile", align: "left" },
    { key: "username", label: "Username", align: "left" },
    { key: "password", label: "Password", align: "left" },
    { key: "nas", label: "NAS", align: "left" },
    { key: "pop", label: "POP", align: "left" },
    { key: "odc", label: "ODC", align: "left" },
    { key: "odp", label: "ODP", align: "left" },
    { key: "mitra", label: "Mitra", align: "left" },
  ];

  /* ---------- Export ---------- */
  const handleExportData = async (e) => {
    e.preventDefault();
    setLoadingExport(true);
    try {
      const res = await PppoeUserService.export({ params: {} });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const cd = res.headers?.["content-disposition"];
      let fileName = "pppoe-users-export.xlsx";
      if (cd) {
        const m = cd.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (m?.[1]) fileName = m[1].replace(/['"]/g, "");
      }
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.log(error);
    } finally {
      setLoadingExport(false);
    }
  };

  /* ============================================
     RENDER
     ============================================ */
  return (
    <>
      <Helmet>
        <title>PPPoE User - Radius Trend</title>
        <meta
          name="description"
          content="Kelola data pengguna PPPoE, monitor status koneksi, dan kelola profile pelanggan ISP."
        />
      </Helmet>

      <DashboardLayout
        showSidebar={showSidebar}
        setShowSidebar={setShowSidebar}
      >
        {/* ===== Header ===== */}
        <Header toggleSidebar={() => setShowSidebar(!showSidebar)}>
          <div className="flex items-center gap-3 flex-wrap">
            <h2 className="text-base md:text-xl font-bold text-gray-100">
              Management PPPoE User
            </h2>
            {meta && (
              <span className="hidden sm:inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-cyan-500/10 text-cyan-400 border border-cyan-500/20">
                {meta.total} data
              </span>
            )}
            {selectedIds.length > 0 && (
              <span className="hidden sm:inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-500/10 text-red-400 border border-red-500/20">
                {selectedIds.length} dipilih
              </span>
            )}
          </div>

          <div className="flex gap-2">
            <BtnDefault onClick={() => setOpenImport(true)} variant="secondary">
              <span className="hidden lg:flex items-center gap-2">
                <FaFileImport className="text-xs" /> Import
              </span>
              <FaFileImport className="lg:hidden text-xs" />
            </BtnDefault>
            <BtnDefault onClick={handleExportData} disabled={loadingExport}>
              <span className="hidden lg:flex items-center gap-2">
                <FaFileExport className="text-xs" />
                {loadingExport ? "Exporting..." : "Export"}
              </span>
              <FaFileExport className="lg:hidden text-xs" />
            </BtnDefault>
            <BtnDefault
              onClick={() => navigate("/dashboard/pppoe/user/create")}
            >
              <span className="hidden lg:flex items-center gap-2">
                <FaPlus className="text-xs" /> Tambah
              </span>
              <FaPlus className="lg:hidden" />
            </BtnDefault>
          </div>
        </Header>

        {/* ===== Content ===== */}
        <main className="px-4 pb-4  lg:px-6 lg:pb-6 space-y-5">
          {/* ── Search Bar + Filter Button (ringkas) ── */}
          <div className="flex items-center gap-3 sticky top-16 z-10 py-5 bg-navy-900">
            {/* Search */}
            <div className="relative flex-1">
              <FaMagnifyingGlass className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-500 text-sm" />
              <input
                type="text"
                placeholder="Cari nama, ID pelanggan, username..."
                value={params?.search}
                onChange={(e) =>
                  setParams((p) => ({ ...p, search: e.target.value, page: 1 }))
                }
                className="w-full pl-10 pr-4 py-2.5 text-sm rounded-xl bg-slate-800/60 border border-slate-600/40 text-gray-100 placeholder-slate-500 outline-none focus:border-cyan-500/50 focus:ring-1 focus:ring-cyan-500/20 transition-all"
              />
            </div>

            {/* Filter Button */}
            <button
              onClick={() => setOpenFilter(true)}
              className="relative flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold transition-all"
              style={{
                background:
                  activeFilterCount > 0
                    ? "rgba(0, 180, 216, 0.12)"
                    : "rgba(51, 65, 85, 0.2)",
                border:
                  activeFilterCount > 0
                    ? "1px solid rgba(0, 180, 216, 0.3)"
                    : "1px solid rgba(51, 65, 85, 0.3)",
                color: activeFilterCount > 0 ? "#22d3ee" : "#94a3b8",
              }}
            >
              <FaFilter className="text-xs" />
              <span className="hidden sm:inline">Filter</span>

              {/* Badge count */}
              {activeFilterCount > 0 && (
                <span
                  className="flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-black"
                  style={{
                    background: "#22d3ee",
                    color: "#0f172a",
                  }}
                >
                  {activeFilterCount}
                </span>
              )}
            </button>

            {/* Quick Reset (hanya muncul kalau ada filter aktif) */}
            {activeFilterCount > 0 && (
              <button
                onClick={handleFilterReset}
                className="flex items-center gap-1.5 px-3 py-2.5 rounded-xl text-xs font-semibold text-red-400 transition-all"
                style={{
                  background: "rgba(239, 68, 68, 0.06)",
                  border: "1px solid rgba(239, 68, 68, 0.15)",
                }}
                title="Hapus semua filter"
              >
                <FaRotateLeft size={11} />
                <span className="hidden sm:inline">Reset</span>
              </button>
            )}
          </div>

          {/* ── Active Filter Chips ── */}
          {activeFilterCount > 0 && (
            <div className="flex flex-wrap items-center gap-2">
              {params.nas_id && (
                <FilterChip
                  label="NAS"
                  onRemove={() =>
                    setParams((p) => ({
                      ...p,
                      nas_id: "",
                      pop_id: "",
                      odc_id: "",
                      odp_id: "",
                      page: 1,
                    }))
                  }
                />
              )}
              {params.pop_id && (
                <FilterChip
                  label="POP"
                  onRemove={() =>
                    setParams((p) => ({
                      ...p,
                      pop_id: "",
                      odc_id: "",
                      odp_id: "",
                      page: 1,
                    }))
                  }
                />
              )}
              {params.odc_id && (
                <FilterChip
                  label="ODC"
                  onRemove={() =>
                    setParams((p) => ({
                      ...p,
                      odc_id: "",
                      odp_id: "",
                      page: 1,
                    }))
                  }
                />
              )}
              {params.odp_id && (
                <FilterChip
                  label="ODP"
                  onRemove={() =>
                    setParams((p) => ({ ...p, odp_id: "", page: 1 }))
                  }
                />
              )}
              {params.profile_id && (
                <FilterChip
                  label="Profile"
                  onRemove={() =>
                    setParams((p) => ({ ...p, profile_id: "", page: 1 }))
                  }
                />
              )}
              {params.status && (
                <FilterChip
                  label={`Status: ${params.status}`}
                  onRemove={() =>
                    setParams((p) => ({ ...p, status: "", page: 1 }))
                  }
                />
              )}
              {params.type && (
                <FilterChip
                  label={`Tipe: ${params.type}`}
                  onRemove={() =>
                    setParams((p) => ({ ...p, type: "", page: 1 }))
                  }
                />
              )}
            </div>
          )}

          {/* ── Desktop Table ── */}
          <section
            className="hidden lg:block rounded-2xl overflow-hidden"
            style={{
              background: "rgba(15, 23, 42, 0.5)",
              border: "1px solid rgba(51, 65, 85, 0.3)",
            }}
          >
            <div className="overflow-x-auto">
              <table className="min-w-max max-w-2xl text-sm">
                <thead>
                  <tr
                    className="border-b"
                    style={{
                      background: "rgba(15, 23, 42, 0.7)",
                      borderColor: "rgba(51, 65, 85, 0.3)",
                    }}
                  >
                    <th className="px-4 py-3.5 text-center w-12">
                      <input
                        type="checkbox"
                        checked={
                          selectedIds.length === data.length && data.length > 0
                        }
                        onChange={toggleSelectAll}
                        className="w-4 h-4 rounded border-slate-600 bg-slate-800 text-cyan-500 focus:ring-cyan-500/30 focus:ring-offset-0 cursor-pointer"
                      />
                    </th>
                    {columns.map((col) => (
                      <th
                        key={col.key}
                        className={`px-4 py-3.5 text-xs font-semibold uppercase tracking-wider text-slate-400 ${col.align === "center" ? "text-center" : "text-left"}`}
                        style={col.width ? { width: col.width } : undefined}
                      >
                        {col.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody
                  className="divide-y"
                  style={{ borderColor: "rgba(51, 65, 85, 0.15)" }}
                >
                  {loading && !data && (
                    <>
                      {[...Array(5)].map((_, i) => (
                        <SkeletonRow key={i} cols={columns.length} />
                      ))}
                    </>
                  )}

                  {!loading && data?.length === 0 && (
                    <tr>
                      <td
                        colSpan={columns.length + 1}
                        className="px-4 py-16 text-center"
                      >
                        <div className="flex flex-col items-center gap-3">
                          <div
                            className="w-16 h-16 rounded-2xl flex items-center justify-center"
                            style={{
                              background: "rgba(51, 65, 85, 0.2)",
                              border: "1px solid rgba(51, 65, 85, 0.2)",
                            }}
                          >
                            <FaUser className="text-2xl text-slate-600" />
                          </div>
                          <div>
                            <p className="text-slate-400 font-medium">
                              Tidak ada data ditemukan
                            </p>
                            <p className="text-xs text-slate-500 mt-1">
                              {params?.search || activeFilterCount > 0
                                ? "Coba ubah filter atau kata kunci pencarian"
                                : "Belum ada pengguna PPPoE yang terdaftar"}
                            </p>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}

                  {!loading &&
                    data?.map((item, index) => (
                      <tr
                        key={item.id}
                        className="cursor-pointer transition-colors duration-150 hover:bg-cyan-500/[0.03]"
                      >
                        <td className="px-4 py-3 text-center">
                          <input
                            type="checkbox"
                            checked={selectedIds.includes(item.id)}
                            onChange={() => toggleSelect(item.id)}
                            onClick={(e) => e.stopPropagation()}
                            className="w-4 h-4 rounded border-slate-600 bg-slate-800 text-cyan-500 focus:ring-cyan-500/30 focus:ring-offset-0 cursor-pointer"
                          />
                        </td>
                        <td className="px-4 py-3 text-center text-slate-500 font-mono text-xs">
                          {meta.from + index}
                        </td>
                        <td className="px-4 py-3 text-center ">
                          <button
                            type="button"
                            onClick={() => {
                              setSelectedStatus(item.status);
                              setSelectedId(item.id);
                              setOpenUpdateStatus(true);
                            }}
                            className="capitalize"
                          >
                            <StatusBadge status={item.status} />
                          </button>
                        </td>
                        <td className="px-4 py-3 text-center">
                          {item?.status === "aktif" &&
                          item?.internet_status === "online" ? (
                            <InternetStatus />
                          ) : item?.status === "nonaktif" ||
                            (item?.status === "aktif" &&
                              item?.internet_status === "offline") ? (
                            <MdOutlineWifiOff className="text-xl text-red-500" />
                          ) : (
                            <MdOutlineWifiOff className="text-xl text-amber-500" />
                          )}
                        </td>
                        <td
                          onClick={() =>
                            navigate(`/dashboard/pppoe/user/detail/${item.id}`)
                          }
                          className="px-4 py-3"
                        >
                          <span className="font-mono text-xs text-cyan-400/80 bg-cyan-500/8 px-2 py-0.5 rounded">
                            {item.internet_number}
                          </span>
                        </td>
                        <td
                          onClick={() =>
                            navigate(`/dashboard/pppoe/user/detail/${item.id}`)
                          }
                          className="px-4 py-3 min-w-[200px]"
                        >
                          <span className="font-semibold text-gray-100">
                            {item.name}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-slate-300 min-w-[150px]">
                          {item.profile?.name || "-"}
                        </td>
                        <td
                          className="px-4 py-3 text-slate-300 cursor-pointer hover:text-cyan-400 transition-colors group"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleCopy(item?.username ?? "", "Username");
                          }}
                          title="Klik untuk copy"
                        >
                          <span className="flex items-center gap-1.5">
                            {item?.username || "-"}
                            {item?.username && (
                              <FaCheck className="text-[10px] text-emerald-400 opacity-0 group-hover:opacity-40 transition-opacity" />
                            )}
                          </span>
                        </td>
                        <td
                          className="px-4 py-3 text-slate-300 cursor-pointer hover:text-cyan-400 transition-colors group"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleCopy(item?.password ?? "", "Password");
                          }}
                          title="Klik untuk copy"
                        >
                          <span className="flex items-center gap-1.5">
                            {item?.password || "-"}
                            {item?.password && (
                              <FaCheck className="text-[10px] text-emerald-400 opacity-0 group-hover:opacity-40 transition-opacity" />
                            )}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-slate-300">
                          {item.nas?.shortname || "-"}
                        </td>
                        <td className="px-4 py-3 text-slate-300">
                          {item.pop?.name || "-"}
                        </td>
                        <td className="px-4 py-3 text-slate-300">
                          {item?.odc?.name || "-"}
                        </td>
                        <td className="px-4 py-3 text-slate-300">
                          {item.odp?.name || "-"}
                        </td>
                        <td className="px-4 py-3 text-slate-400 italic text-xs">
                          {item?.mitra?.name || "—"}
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <Pagination meta={meta} params={params} setParams={setParams} />
          </section>

          {/* ── Mobile Card List ── */}
          <section className="lg:hidden space-y-3">
            {loading && !data && (
              <>
                {[...Array(4)].map((_, i) => (
                  <SkeletonCard key={i} />
                ))}
              </>
            )}
            {!loading && data?.length === 0 && (
              <div
                className="rounded-2xl p-12 text-center"
                style={{
                  background: "rgba(15, 23, 42, 0.5)",
                  border: "1px solid rgba(51, 65, 85, 0.3)",
                }}
              >
                <div
                  className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4"
                  style={{
                    background: "rgba(51, 65, 85, 0.2)",
                    border: "1px solid rgba(51, 65, 85, 0.2)",
                  }}
                >
                  <FaUser className="text-2xl text-slate-600" />
                </div>
                <p className="text-slate-400 font-medium">
                  Tidak ada data ditemukan
                </p>
                <p className="text-xs text-slate-500 mt-1">
                  {params?.search || activeFilterCount > 0
                    ? "Coba ubah filter pencarian"
                    : "Belum ada data PPPoE"}
                </p>
              </div>
            )}
            {!loading &&
              data?.map((item) => (
                <article
                  key={item.id}
                  onClick={() =>
                    navigate(`/dashboard/pppoe/user/detail/${item.id}`)
                  }
                  className="rounded-2xl p-4 active:scale-[0.98] transition-all duration-200 cursor-pointer"
                  style={{
                    background: "rgba(15, 23, 42, 0.5)",
                    border: "1px solid rgba(51, 65, 85, 0.3)",
                  }}
                >
                  <div className="flex justify-between items-start mb-3">
                    <div className="flex items-center gap-3">
                      <div
                        className="w-10 h-10 rounded-xl flex items-center justify-center font-bold text-sm"
                        style={{
                          background:
                            "linear-gradient(135deg, rgba(0, 180, 216, 0.15), rgba(0, 119, 182, 0.08))",
                          border: "1px solid rgba(0, 180, 216, 0.15)",
                          color: "#48CAE4",
                        }}
                      >
                        {item.name.charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-100 leading-tight text-sm">
                          {item.name}
                        </h3>
                        <p className="text-xs text-slate-500 font-mono mt-0.5">
                          {item.internet_number}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {item?.status === "aktif" &&
                      item?.internet_status === "online" ? (
                        <InternetStatus />
                      ) : item?.status === "nonaktif" ||
                        (item?.status === "aktif" &&
                          item?.internet_status === "offline") ? (
                        <MdOutlineWifiOff className="text-xl text-red-500" />
                      ) : (
                        <MdOutlineWifiOff className="text-xl text-amber-500" />
                      )}
                      <div
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedStatus(item.status);
                          setSelectedId(item.id);
                          setOpenUpdateStatus(true);
                        }}
                      >
                        <StatusBadge status={item.status} />
                      </div>
                    </div>
                  </div>
                  <div
                    className="grid grid-cols-2 gap-x-4 gap-y-2.5 pt-3"
                    style={{ borderTop: "1px solid rgba(51, 65, 85, 0.15)" }}
                  >
                    <div>
                      <p className="text-[10px] text-slate-500 uppercase font-semibold tracking-wider">
                        Profile
                      </p>
                      <p className="text-sm text-slate-300 mt-0.5 truncate">
                        {item.profile?.name || "-"}
                      </p>
                    </div>
                    <div>
                      <p className="text-[10px] text-slate-500 uppercase font-semibold tracking-wider">
                        NAS
                      </p>
                      <p className="text-sm text-slate-300 mt-0.5 truncate">
                        {item.nas?.name || "-"}
                      </p>
                    </div>
                    <div
                      onClick={(e) => {
                        e.stopPropagation();
                        handleCopy(item?.username ?? "", "Username");
                      }}
                      className="cursor-pointer active:bg-slate-700/30 -mx-2 px-2 rounded-lg transition-colors"
                    >
                      <p className="text-[10px] text-slate-500 uppercase font-semibold tracking-wider">
                        Username
                      </p>
                      <p className="text-sm text-slate-300 mt-0.5 truncate">
                        {item?.username || "-"}
                      </p>
                    </div>
                    <div
                      onClick={(e) => {
                        e.stopPropagation();
                        handleCopy(item?.password ?? "", "Password");
                      }}
                      className="cursor-pointer active:bg-slate-700/30 -mx-2 px-2 rounded-lg transition-colors"
                    >
                      <p className="text-[10px] text-slate-500 uppercase font-semibold tracking-wider">
                        Password
                      </p>
                      <p className="text-sm text-slate-300 mt-0.5 truncate">
                        {item?.password || "-"}
                      </p>
                    </div>
                    <div>
                      <p className="text-[10px] text-slate-500 uppercase font-semibold tracking-wider">
                        POP / ODP
                      </p>
                      <p className="text-sm text-slate-300 mt-0.5 truncate">
                        {item.pop?.name || "-"} / {item.odp?.name || "-"}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-[10px] text-slate-500 uppercase font-semibold tracking-wider">
                        Mitra
                      </p>
                      <p className="text-sm text-slate-400 italic mt-0.5 truncate">
                        {item?.mitra?.name || "—"}
                      </p>
                    </div>
                  </div>
                </article>
              ))}
            {meta?.last_page > 1 && (
              <div
                className="flex items-center justify-between pt-3 pb-6 px-1"
                style={{ borderTop: "1px solid rgba(51, 65, 85, 0.15)" }}
              >
                <button
                  onClick={() =>
                    setParams((p) => ({ ...p, page: meta.current_page - 1 }))
                  }
                  disabled={meta.current_page === 1}
                  className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium text-slate-300 disabled:opacity-30 disabled:cursor-not-allowed"
                  style={{
                    background: "rgba(15, 23, 42, 0.5)",
                    border: "1px solid rgba(51, 65, 85, 0.3)",
                  }}
                >
                  <FaChevronLeft className="text-xs" /> Sebelumnya
                </button>
                <span className="text-sm font-medium text-slate-400">
                  <span className="text-cyan-400">{meta.current_page}</span>
                  <span className="text-slate-600 mx-1">/</span>
                  {meta.last_page}
                </span>
                <button
                  onClick={() =>
                    setParams((p) => ({ ...p, page: meta.current_page + 1 }))
                  }
                  disabled={meta.current_page === meta.last_page}
                  className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium text-slate-300 disabled:opacity-30 disabled:cursor-not-allowed"
                  style={{
                    background: "rgba(15, 23, 42, 0.5)",
                    border: "1px solid rgba(51, 65, 85, 0.3)",
                  }}
                >
                  Berikutnya <FaChevronRight className="text-xs" />
                </button>
              </div>
            )}
          </section>
        </main>
      </DashboardLayout>

      {/* ===== Drawers & Modals ===== */}
      <FilterDrawer
        open={openFilter}
        setOpen={setOpenFilter}
        currentParams={params}
        onApply={handleFilterApply}
      />

      <PppoeUpdateStatus
        id={selectedId}
        status={selectedStatus}
        openModal={openUpdateStatus}
        setOpenModal={setOpenUpdateStatus}
        refresh={refreshData}
      />
      <ImportPppoeUser
        openModal={openImport}
        setOpenModal={setOpenImport}
        refresh={refreshData}
      />
      <CopyToast show={copied} label={copyLabel} />
    </>
  );
};

/* ============================================
   KOMPONEN: FilterChip (di luar export default)
   ============================================ */
const FilterChip = ({ label, onRemove }) => (
  <span
    className="inline-flex items-center gap-1.5 pl-2.5 pr-1.5 py-1 rounded-lg text-[11px] font-semibold cursor-default"
    style={{
      background: "rgba(0, 180, 216, 0.08)",
      border: "1px solid rgba(0, 180, 216, 0.2)",
      color: "#22d3ee",
    }}
  >
    {label}
    <button
      onClick={onRemove}
      className="p-0.5 rounded-md hover:bg-cyan-400/20 transition-colors"
    >
      <FaXmark size={10} />
    </button>
  </span>
);
